import { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCreateCheckoutSessionMutation } from 'store/api/apiSlice';
import { OrderPreviewLineItemRequest } from 'store/api/apiTypes';
import { Loader } from 'shared/ui/loader/loader';
import cls from './checkout.module.scss';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const STRIPE_READY_FALLBACK_MS = 3000;

interface StripePaymentProps {
  items: OrderPreviewLineItemRequest[];
  onSuccess?: (sessionId?: string) => void;
}

export const StripePayment: FC<StripePaymentProps> = ({ items, onSuccess }) => {
  const [createCheckoutSession, sessionResult] =
    useCreateCheckoutSessionMutation();
  const [isStripeReady, setIsStripeReady] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!stripePromise) return;
    createCheckoutSession(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const errorMessage = useMemo(() => {
    const err = sessionResult.error;
    if (!err) return null;
    if (typeof err === 'object' && 'data' in err) {
      const data = (err as { data?: { error?: string } }).data;
      if (data?.error) return data.error;
    }
    return 'Failed to init Stripe';
  }, [sessionResult.error]);

  const clientSecret = sessionResult.data?.client_secret;

  // Track when the Stripe iframe finishes loading so we can hide the overlay
  // Loader. The iframe is cross-origin, so we watch for its insertion via
  // MutationObserver and hook into its `load` event; a fallback timer covers
  // the case where the load event fires before we attach the listener.
  useEffect(() => {
    if (!clientSecret) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;
    setIsStripeReady(false);

    let removeLoad: (() => void) | undefined;
    const fallback = setTimeout(
      () => setIsStripeReady(true),
      STRIPE_READY_FALLBACK_MS
    );

    const observer = new MutationObserver(() => {
      const iframe = container.querySelector('iframe');
      if (!iframe) return;
      observer.disconnect();
      const handleLoad = () => {
        clearTimeout(fallback);
        setIsStripeReady(true);
      };
      iframe.addEventListener('load', handleLoad);
      removeLoad = () => iframe.removeEventListener('load', handleLoad);
    });
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      clearTimeout(fallback);
      observer.disconnect();
      removeLoad?.();
    };
  }, [clientSecret]);

  if (!stripePromise) {
    return (
      <div className={cls.errorState}>
        Stripe is not configured: public key is missing.
      </div>
    );
  }

  if (errorMessage) {
    return <div className={cls.errorState}>{errorMessage}</div>;
  }

  if (sessionResult.isLoading || !clientSecret) {
    return (
      <div className={cls.loadingState}>
        <Loader size={24} color='#000' />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cls.stripeContainer}>
      {!isStripeReady && (
        <div className={cls.stripeLoaderOverlay}>
          <Loader size={24} color='#000' />
        </div>
      )}
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{
          clientSecret,
          onComplete: () => onSuccess?.(sessionResult.data?.session_id),
        }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};
